import type { ReactNode } from "react";
import {
  useAdminAddOnRequests,
  useAdminContactSubmissions,
  useAdminNewsletterSubscribers,
} from "@/hooks/useAdminLeads";
import { Button } from "@/components/ui/button";
import { AdminPanelHeader } from "./AdminPanelHeader";
import { CircleAlert, Database, Mail, MessageSquare, RefreshCw, ShoppingBag } from "lucide-react";

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatTimestamp(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
}

function formatAmount(value: number | null | undefined, currency = "USD") {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

function formatStatus(value: string | null | undefined) {
  if (!value) return "Unknown";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getStatusClasses(value: string | null | undefined) {
  const normalized = value?.toLowerCase();
  if (
    normalized === "paid" ||
    normalized === "succeeded" ||
    normalized === "complete" ||
    normalized === "completed"
  ) {
    return "bg-green-100 text-green-700";
  }
  if (
    normalized === "pending" ||
    normalized === "processing" ||
    normalized === "requires_action"
  ) {
    return "bg-yellow-100 text-yellow-700";
  }
  return "bg-gray-100 text-gray-600";
}

function shortenId(value: string | null | undefined) {
  if (!value) return "—";
  if (value.length <= 14) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function extractMissingRelation(message: string | null) {
  if (!message) return null;
  const match = message.match(/relation "([^"]+)" does not exist/i);
  return match?.[1] ?? null;
}

function getDatasetStatus(message: string | null): "live" | "setup" | "error" {
  if (!message) return "live";
  if (extractMissingRelation(message)) return "setup";
  return "error";
}

function getBadgeClasses(status: "live" | "setup" | "error") {
  if (status === "live") return "bg-green-50 text-green-700";
  if (status === "setup") return "bg-amber-50 text-amber-700";
  return "bg-red-50 text-red-700";
}

function StatCard({
  icon,
  label,
  value,
  hint,
  status,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
  status: "live" | "setup" | "error";
}) {
  const accentClasses =
    status === "live"
      ? "bg-[#fff4f8] text-pink-dark"
      : status === "setup"
        ? "bg-amber-50 text-amber-700"
        : "bg-red-50 text-red-700";

  return (
    <div className="rounded-[1.4rem] border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(31,10,24,0.05)]">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${accentClasses}`}>
          {icon}
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.08em] ${getBadgeClasses(status)}`}>
          {status === "live" ? "Live" : status === "setup" ? "Setup" : "Error"}
        </span>
      </div>
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      <div className="mt-1 text-xs text-gray-400">{hint}</div>
    </div>
  );
}

function Section({
  title,
  description,
  badgeLabel,
  badgeTone = "live",
  children,
}: {
  title: string;
  description: string;
  badgeLabel: string;
  badgeTone?: "live" | "setup" | "error";
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-[1.6rem] border border-gray-200 bg-white shadow-[0_14px_40px_rgba(31,10,24,0.05)]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${getBadgeClasses(badgeTone)}`}>
          {badgeLabel}
        </span>
      </div>
      {children}
    </section>
  );
}

function LoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-gray-400">
        Loading…
      </td>
    </tr>
  );
}

function ErrorRow({
  colSpan,
  message,
}: {
  colSpan: number;
  message: string;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-red-600">
        {message}
      </td>
    </tr>
  );
}

function EmptyState({
  title,
  description,
  tone = "neutral",
  detail,
}: {
  title: string;
  description: string;
  tone?: "neutral" | "setup" | "error";
  detail?: string | null;
}) {
  const toneClasses =
    tone === "setup"
      ? "border-amber-200 bg-amber-50/60 text-amber-900"
      : tone === "error"
        ? "border-red-200 bg-red-50/60 text-red-900"
        : "border-gray-200 bg-gray-50 text-gray-700";

  return (
    <div className="px-5 py-6">
      <div className={`rounded-[1.2rem] border px-4 py-5 ${toneClasses}`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/80">
            {tone === "setup" ? (
              <Database className="h-4 w-4" />
            ) : (
              <CircleAlert className="h-4 w-4" />
            )}
          </div>
          <div>
            <div className="font-semibold">{title}</div>
            <div className="mt-1 text-sm opacity-80">{description}</div>
            {detail && (
              <div className="mt-3 inline-flex rounded-full bg-white/80 px-3 py-1 font-mono text-xs">
                {detail}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function LeadInbox() {
  const addOnRequests = useAdminAddOnRequests();
  const newsletterSubscribers = useAdminNewsletterSubscribers();
  const contactSubmissions = useAdminContactSubmissions();

  const isRefreshing =
    addOnRequests.loading ||
    newsletterSubscribers.loading ||
    contactSubmissions.loading;

  const refreshAll = () => {
    addOnRequests.refetch();
    newsletterSubscribers.refetch();
    contactSubmissions.refetch();
  };

  const addOnCount = addOnRequests.data?.length ?? 0;
  const newsletterCount = newsletterSubscribers.data?.length ?? 0;
  const contactCount = contactSubmissions.data?.length ?? 0;

  const addOnStatus = getDatasetStatus(addOnRequests.error);
  const newsletterStatus = getDatasetStatus(newsletterSubscribers.error);
  const contactStatus = getDatasetStatus(contactSubmissions.error);

  const newsletterMissingRelation = extractMissingRelation(newsletterSubscribers.error);
  const contactMissingRelation = extractMissingRelation(contactSubmissions.error);

  const setupItems = [
    newsletterMissingRelation,
    contactMissingRelation,
  ].filter((value): value is string => Boolean(value));

  return (
    <div className="space-y-6">
      <AdminPanelHeader
        inset
        title="Leads & Requests"
        description="Review add-on buyers, newsletter signups, and contact form messages in one place."
        action={
          <Button
            type="button"
            variant="outline"
            onClick={refreshAll}
            disabled={isRefreshing}
            className="border-[#e5d7e0] text-[#5d2449] hover:bg-[#fbf4f8]"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {setupItems.length > 0 && (
        <div className="rounded-[1.6rem] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,248,233,0.95),rgba(255,252,245,0.98))] p-5 shadow-[0_14px_40px_rgba(242,164,53,0.10)]">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-amber-700 shadow-sm">
              <Database className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-[#5f410d]">
                Some lead sections are ready in the UI but not set up in this database yet
              </h3>
              <p className="mt-1 text-sm text-[#7b5b24]">
                Newsletter and contact submissions need their backing tables before this admin view can show real data.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {setupItems.map((item) => (
                  <span
                    key={item}
                    className="rounded-full bg-white px-3 py-1 font-mono text-xs text-[#7b5b24] shadow-sm"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          icon={<ShoppingBag className="h-5 w-5" />}
          label="Add-On Buyers"
          value={addOnStatus === "live" ? String(addOnCount) : "—"}
          hint={
            addOnStatus === "live"
              ? "Stored add-on purchase records"
              : "Could not load add-on purchase records"
          }
          status={addOnStatus}
        />
        <StatCard
          icon={<Mail className="h-5 w-5" />}
          label="Newsletter"
          value={newsletterStatus === "live" ? String(newsletterCount) : "—"}
          hint={
            newsletterStatus === "live"
              ? "People subscribed through the site"
              : newsletterStatus === "setup"
                ? "Table is not set up in this database yet"
                : "Could not load newsletter signups"
          }
          status={newsletterStatus}
        />
        <StatCard
          icon={<MessageSquare className="h-5 w-5" />}
          label="Contact Messages"
          value={contactStatus === "live" ? String(contactCount) : "—"}
          hint={
            contactStatus === "live"
              ? "Messages sent from the contact page"
              : contactStatus === "setup"
                ? "Table is not set up in this database yet"
                : "Could not load contact submissions"
          }
          status={contactStatus}
        />
      </div>

      <Section
        title="Add-On Buyers"
        description="Purchase records for customers who bought an add-on during checkout."
        badgeLabel={addOnStatus === "live" ? `${addOnCount} total` : "Error"}
        badgeTone={addOnStatus}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-fixed text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs uppercase tracking-[0.08em] text-gray-500">
              <tr>
                <th className="w-[18%] px-4 py-3">Add-On</th>
                <th className="w-[24%] px-4 py-3">Buyer</th>
                <th className="w-[10%] px-4 py-3">Paid</th>
                <th className="w-[12%] px-4 py-3">Status</th>
                <th className="w-[20%] px-4 py-3">Payment Intent</th>
                <th className="w-[16%] px-4 py-3">Purchased</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {addOnRequests.loading && <LoadingRow colSpan={6} />}
              {!addOnRequests.loading && addOnRequests.error && (
                <ErrorRow colSpan={6} message={addOnRequests.error} />
              )}
              {!addOnRequests.loading &&
                !addOnRequests.error &&
                (addOnRequests.data ?? []).map((request) => (
                  <tr key={request.id} className="align-top hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {request.add_on_id?.name ?? "Unknown add-on"}
                      </div>
                      {request.add_on_id?.price_label && (
                        <div className="text-xs text-gray-500">
                          {request.add_on_id.price_label}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <div
                        className="font-medium text-gray-900"
                        title={request.purchase_id?.user_id ?? undefined}
                      >
                        {request.buyer_name ?? shortenId(request.purchase_id?.user_id)}
                      </div>
                      {request.buyer_email && (
                        <div
                          className="mt-0.5 truncate text-xs text-gray-500"
                          title={request.buyer_email}
                        >
                          {request.buyer_email}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {formatAmount(
                        request.price_paid,
                        request.purchase_id?.currency ?? "USD",
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(request.purchase_id?.status)}`}
                      >
                        {formatStatus(request.purchase_id?.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="max-w-[12rem] truncate font-mono text-xs text-gray-500"
                        title={request.purchase_id?.payment_intent_id ?? undefined}
                      >
                        {request.purchase_id?.payment_intent_id ?? "—"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                      {formatTimestamp(request.created_at ?? request.purchase_id?.created_at)}
                    </td>
                  </tr>
                ))}
              {!addOnRequests.loading &&
                !addOnRequests.error &&
                (addOnRequests.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        title="No add-on buyers yet"
                        description="When customers buy an add-on during checkout, their purchase details will show up here."
                      />
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Newsletter Subscribers"
        description="Everyone who asked to receive newsletter updates."
        badgeLabel={
          newsletterStatus === "live"
            ? `${newsletterCount} total`
            : newsletterStatus === "setup"
              ? "Setup needed"
              : "Error"
        }
        badgeTone={newsletterStatus}
      >
        {newsletterSubscribers.loading ? (
          <div className="px-5 py-8 text-center text-gray-400">Loading…</div>
        ) : newsletterStatus === "setup" ? (
          <EmptyState
            tone="setup"
            title="Newsletter storage is not set up yet"
            description="This admin section is ready, but the newsletter subscribers table does not exist in the current database."
            detail={newsletterMissingRelation}
          />
        ) : newsletterStatus === "error" ? (
          <EmptyState
            tone="error"
            title="Could not load newsletter subscribers"
            description="There was a problem reading newsletter records from the database."
            detail={newsletterSubscribers.error}
          />
        ) : newsletterCount === 0 ? (
          <EmptyState
            title="No newsletter subscribers yet"
            description="New email signups will appear here once someone joins the newsletter."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase tracking-[0.08em] text-gray-500">
                <tr>
                  <th className="w-[65%] px-4 py-3">Email</th>
                  <th className="w-[35%] px-4 py-3">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(newsletterSubscribers.data ?? []).map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="break-all px-4 py-3 font-medium text-gray-900">
                      {subscriber.email}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatTimestamp(subscriber.subscribed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Contact Submissions"
        description="Messages submitted through the public contact form."
        badgeLabel={
          contactStatus === "live"
            ? `${contactCount} total`
            : contactStatus === "setup"
              ? "Setup needed"
              : "Error"
        }
        badgeTone={contactStatus}
      >
        {contactSubmissions.loading ? (
          <div className="px-5 py-8 text-center text-gray-400">Loading…</div>
        ) : contactStatus === "setup" ? (
          <EmptyState
            tone="setup"
            title="Contact submissions storage is not set up yet"
            description="The contact admin table does not exist in the current database, so this section cannot load messages yet."
            detail={contactMissingRelation}
          />
        ) : contactStatus === "error" ? (
          <EmptyState
            tone="error"
            title="Could not load contact submissions"
            description="There was a problem reading contact form data from the database."
            detail={contactSubmissions.error}
          />
        ) : contactCount === 0 ? (
          <EmptyState
            title="No contact submissions yet"
            description="Once someone sends a message through the contact page, it will show up here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] table-fixed text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase tracking-[0.08em] text-gray-500">
                <tr>
                  <th className="w-[26%] px-4 py-3">Contact</th>
                  <th className="w-[18%] px-4 py-3">Subject</th>
                  <th className="w-[38%] px-4 py-3">Message</th>
                  <th className="w-[18%] px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(contactSubmissions.data ?? []).map((submission) => (
                  <tr key={submission.id} className="align-top hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{submission.name}</div>
                      <div className="break-all text-sm text-gray-500">{submission.email}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="break-words">{submission.subject?.trim() || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="whitespace-pre-wrap break-words">
                        {submission.message}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatTimestamp(submission.submitted_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

export default LeadInbox;
